<template>
    <div class="box m-b-0 box-modal">
        <div class="box-header">
            <h3>{{ evaluationId ? 'Edytuj ocenę dostawcy' : 'Dodaj nową ocenę dostawcy' }}</h3>
            <span class="text-muted " v-if="stockOrder">Dotyczy dostawy <strong>{{ stockOrder.documentNo }}</strong></span>
        </div>
        <div class="box-tool">
            <ul class="nav">
                <li class="nav-item inline icon-with-desc">
                    <a class="nav-link text-muted" @click="saveAndExit()">
                        <i class="material-icons md-18">done</i>
                        <span class="icon-desc">CTRL+S</span>
                    </a>
                </li>
                <li class="nav-item inline icon-with-desc">
                    <a class="nav-link text-muted" @click="$emit('close')">
                        <i class="material-icons md-18">clear</i>
                        <span class="icon-desc">ESC</span>
                    </a>
                </li>
            </ul>
        </div>
        <hr class="m-y-0">

        <div style="min-height:140px;" class="box-body p-t-0">
            <!-- Informacje o zamówieniu -->
            <div class="row m-b" v-if="stockOrder">
                <div class="col-md-12">
                    <div class="panel panel-default">
                        <div class="panel-heading">
                            <strong>Informacje o zamówieniu</strong>
                        </div>
                        <div class="panel-body">
                            <div class="row">
                                <div class="col-md-6">
                                    <div class="m-b-sm">
                                        <span class="text-muted">Numer dokumentu:</span>
                                        <strong class="m-l-sm">{{ stockOrder.documentNo }}</strong>
                                    </div>
                                    <div class="m-b-sm">
                                        <span class="text-muted">Dostawca:</span>
                                        <strong class="m-l-sm">{{ stockOrder.contractor.name }}</strong>
                                    </div>
                                    <div class="m-b-sm" v-if="stockOrder.contractor.address">
                                        <span class="text-muted">Adres:</span>
                                        <span class="m-l-sm">{{ stockOrder.contractor.address }}</span>
                                    </div>
                                    <div class="m-b-sm" v-if="stockOrder.contractor.taxNo">
                                        <span class="text-muted">NIP:</span>
                                        <span class="m-l-sm">{{ stockOrder.contractor.taxNo }}</span>
                                    </div>
                                </div>
                                <div class="col-md-6">
                                    <div class="m-b-sm">
                                        <span class="text-muted">Status:</span>
                                        <span class="label m-l-sm" :class="getStatusClass(stockOrder.status)">{{ getStatusLabel(stockOrder.status) }}</span>
                                    </div>
                                    <div class="m-b-sm" v-if="stockOrder.realizationDate">
                                        <span class="text-muted">Data realizacji:</span>
                                        <span class="m-l-sm">{{ formatDate(stockOrder.realizationDate) }}</span>
                                    </div>
                                    <div class="m-b-sm" v-if="stockOrder.realizedAt">
                                        <span class="text-muted">Zrealizowano:</span>
                                        <span class="m-l-sm">{{ formatDate(stockOrder.realizedAt) }}</span>
                                    </div>
                                    <div class="m-b-sm">
                                        <span class="text-muted">Data utworzenia:</span>
                                        <span class="m-l-sm">{{ formatDate(stockOrder.createdAt) }}</span>
                                    </div>
                                    <div class="m-b-sm" v-if="stockOrder.invoiceNo">
                                        <span class="text-muted">Nr faktury:</span>
                                        <span class="m-l-sm">{{ stockOrder.invoiceNo }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Formularz oceny -->
            <div class="row">
                <div class="col-md-12">
                    <div class="form-group">
                        <label>Kategoria towaru/usługi <span class="text-danger">*</span></label>
                        <v-select placeholder="Kategoria" taggable v-model="form.goodsCategory" :options="existingCategories" />
                    </div>
                </div>
            </div>

            <div class="row">
                <div class="col-md-12">
                    <div class="form-group">
                        <label>Dzień realizacji dostawy <span class="text-danger">*</span></label>
                        <datepicker
                            :monday-first="true"
                            :bootstrap-styling="true"
                            :typeable="true"
                            clear-button
                            format="yyyy-MM-dd"
                            placeholder="Wybierz datę"
                            v-model="form.deliveryDate"
                            input-class="form-control" />
                    </div>
                </div>
            </div>

            <div class="row" v-if="deliveryEvaluation">
                <div class="col-md-12">
                    <div class="alert alert-warning">
                        <i class="material-icons md-18 v-middle m-r-xs">lightbulb_outline</i>
                        <strong>Sugestia:</strong>
                        Pracownik <strong>{{ deliveryEvaluationAuthorName }}</strong>
                        ocenił jakość dostawy na
                        <strong>{{ deliveryEvaluation.points }}/5</strong>.
                        <span v-if="deliveryEvaluation.comment">
                            Komentarz: <em>“{{ deliveryEvaluation.comment }}”</em>
                        </span><br/>
                        <a class="m-l-sm" href="#" @click.prevent="applyDeliveryEvaluation">Zastosuj</a>
                    </div>
                </div>
            </div>

            <div class="row">
                <div class="col-md-4">
                    <div class="form-group">
                        <label>Ocena jakości <span class="text-danger">*</span></label>
                        <select class="form-control" v-model.number="form.rating.quality">
                            <option :value="1">1 - Bardzo słabo</option>
                            <option :value="2">2 - Słabo</option>
                            <option :value="3">3 - Średnio</option>
                            <option :value="4">4 - Dobrze</option>
                            <option :value="5">5 - Bardzo dobrze</option>
                        </select>
                    </div>
                </div>
                <div class="col-md-4">
                    <div class="form-group">
                        <label>Ocena dostawy <span class="text-danger">*</span></label>
                        <select class="form-control" v-model.number="form.rating.delivery">
                            <option :value="1">1 - Bardzo słabo</option>
                            <option :value="2">2 - Słabo</option>
                            <option :value="3">3 - Średnio</option>
                            <option :value="4">4 - Dobrze</option>
                            <option :value="5">5 - Bardzo dobrze</option>
                        </select>
                    </div>
                </div>
                <div class="col-md-4">
                    <div class="form-group">
                        <label>Ocena ceny <span class="text-danger">*</span></label>
                        <select class="form-control" v-model.number="form.rating.price">
                            <option :value="1">1 - Bardzo słabo</option>
                            <option :value="2">2 - Słabo</option>
                            <option :value="3">3 - Średnio</option>
                            <option :value="4">4 - Dobrze</option>
                            <option :value="5">5 - Bardzo dobrze</option>
                        </select>
                    </div>
                </div>
            </div>

            <div class="row">
                <div class="col-md-12">
                    <div class="form-group">
                        <label>Komentarze</label>
                        <textarea
                            class="form-control"
                            v-model="form.comments"
                            rows="4"
                            placeholder="Dodatkowe uwagi dotyczące oceny..."/>
                    </div>
                </div>
            </div>

            <div class="row" v-if="stockOrder">
                <div class="col-md-12">
                    <div class="alert alert-info">
                        <strong>Suma ocen:</strong> {{ totalScore }} / 15
                    </div>
                </div>
            </div>
        </div>

        <div class="box-footer" v-if="isProcessing">
            <Spinner/>
        </div>
        <div class="box-footer clearfix" v-else>
            <button
                class="btn white none-radius btn-sm"
                :disabled="isProcessing"
                @click="$emit('close')">Anuluj</button>

            <button
                class="pull-right btn info none-radius btn-sm"
                :disabled="isProcessing"
                @click="saveAndExit">Zapisz</button>
        </div>
    </div>
</template>

<script>
import moment from 'moment';
import Api from '../../../Api.js';
import EmployeeManager from '../../../EmployeeManager.js';
import Spinner from '../../Spinner.vue';
import Datepicker from 'vuejs-datepicker';

export default {
    name: 'AddSupplierEvalModal',
    props: {
        stockOrderId: {
            type: String,
            required: false
        },
        evaluationId: {
            type: String,
            required: false
        }
    },
    components: {
        Spinner,
        Datepicker
    },
    data: function () {
        return {
            isProcessing: false,
            stockOrder: null,
            evaluation: null,
            deliveryEvaluationAuthorName: null,
            form: {
                goodsCategory: '',
                deliveryDate: null,
                rating: {
                    quality: 3,
                    delivery: 3,
                    price: 3
                },
                comments: ''
            },
            existingCategories: []
        }
    },
    computed: {
        totalScore() {
            return this.form.rating.quality + this.form.rating.delivery + this.form.rating.price;
        },
        deliveryEvaluation() {
            return this.stockOrder && this.stockOrder.deliveryEvaluation ? this.stockOrder.deliveryEvaluation : null;
        }
    },
    created: async function() {
        // Jeśli edytujemy istniejącą ankietę
        if (this.evaluationId) {
            this.evaluation = await Api.iso.suppliers.evaluations.getById(this.evaluationId);

            // Wypełnij formularz danymi z ankiety
            this.form.goodsCategory = this.evaluation.goodsCategory;
            this.form.deliveryDate = this.evaluation.deliveryDate ? new Date(this.evaluation.deliveryDate) : null;
            this.form.rating.quality = this.evaluation.rating.quality;
            this.form.rating.delivery = this.evaluation.rating.delivery;
            this.form.rating.price = this.evaluation.rating.price;
            this.form.comments = this.evaluation.comments || '';

            // Pobierz stockOrder jeśli jest orderId
            if (this.evaluation.orderId) {
                this.stockOrder = await Api.stock.getOrder(this.evaluation.orderId);
            }
        } else if (this.stockOrderId) {
            // Jeśli tworzymy nową ankietę dla konkretnego zamówienia
            this.stockOrder = await Api.stock.getOrder(this.stockOrderId);
        }

        // pobieramy istniejące kategorie aby podpowiadać użytkownikowi
        this.existingCategories = await Api.iso.suppliers.evaluations.getExistingCategories();

        // pobieramy nazwę pracownika, który ocenił dostawę (jeśli jest)
        if (this.deliveryEvaluation && this.deliveryEvaluation.authorType === 'employee' && this.deliveryEvaluation.authorId) {
            await EmployeeManager.ensureLoaded();
            this.deliveryEvaluationAuthorName = EmployeeManager.getName(this.deliveryEvaluation.authorId) || 'nieznany';
        } else if (this.deliveryEvaluation) {
            this.deliveryEvaluationAuthorName = 'nieznany';
        }
    },
    mounted: function() {
        window.addEventListener('keydown', this.handleKeyUp);
    },
    destroyed: function() {
        window.removeEventListener('keydown', this.handleKeyUp);
    },
    methods: {

        applyDeliveryEvaluation() {
            if (!this.deliveryEvaluation) return;
            this.form.rating.quality = this.deliveryEvaluation.points;
            if (this.deliveryEvaluation.comment) {
                const prefix = this.form.comments ? this.form.comments + '\n\n' : '';
                this.form.comments = prefix + this.deliveryEvaluation.comment;
            }
        },

        saveAndExit: async function() {
            if (!this.validateForm()) {
                return;
            }

            this.isProcessing = true;
            try {
                const data = {
                    goodsCategory: this.form.goodsCategory,
                    deliveryDate: this.form.deliveryDate ? this.form.deliveryDate.toISOString() : undefined,
                    rating: {
                        quality: this.form.rating.quality,
                        delivery: this.form.rating.delivery,
                        price: this.form.rating.price
                    },
                    comments: this.form.comments || undefined
                };

                if (this.evaluationId) {
                    // Edycja istniejącej ankiety
                    await Api.iso.suppliers.evaluations.update(this.evaluationId, data);
                    this.$h.success('Ocena dostawcy została zaktualizowana', 'success');
                } else {
                    // Tworzenie nowej ankiety
                    data.supplierId = this.stockOrder.contractor.customerId;
                    data.orderId = this.stockOrderId;
                    await Api.iso.suppliers.evaluations.create(data);
                    this.$h.success('Ocena dostawcy została zapisana', 'success');
                }

                this.$emit('close', true);
            } catch (error) {
                this.$h.commonApiError(error);
            }
            this.isProcessing = false;
        },

        validateForm() {
            if (!this.form.goodsCategory || !this.form.goodsCategory.trim()) {
                this.$h.info('Kategoria towaru/usługi jest wymagana', 'error');
                return false;
            }

            if (!this.form.deliveryDate) {
                this.$h.info('Dzień realizacji dostawy jest wymagany', 'error');
                return false;
            }

            // Przy edycji nie potrzebujemy stockOrder, przy tworzeniu nowej - tak
            if (!this.evaluationId && (!this.stockOrder || !this.stockOrder.contractor || !this.stockOrder.contractor.customerId)) {
                this.$h.info('Brak informacji o dostawcy', 'error');
                return false;
            }

            return true;
        },

        formatDate(date) {
            if (!date) return '—';
            return moment(date).format('DD.MM.YYYY');
        },

        getStatusLabel(status) {
            const labels = {
                pending: 'Oczekujące',
                canceled: 'Anulowane',
                'partialy-realized': 'Częściowo zrealizowane',
                realized: 'Zrealizowane',
                closed: 'Zamknięte'
            };
            return labels[status] || status;
        },

        getStatusClass(status) {
            const classes = {
                pending: 'label-warning',
                canceled: 'label-danger',
                'partialy-realized': 'label-info',
                realized: 'label-success',
                closed: 'label-default'
            };
            return classes[status] || 'label-default';
        },
        handleKeyUp: function(e) {
            if (e.keyCode == 27) { // ESC
                e.preventDefault();
                this.$emit('close');
            } else if (e.ctrlKey && e.keyCode == 83) { // CTRL+S
                e.preventDefault();
                this.saveAndExit();
            }
        }
    }
}
</script>

<style scoped lang="scss">

</style>
